import type { ColumnDef } from '@tanstack/react-table';
import {
  createActionsColumn,
  createSerialColumn,
  getAreaColumn,
  MemberNameCell,
} from '@/components/common/DataTable';
import { RowViewLink } from '@/components/common/RowActions';
import { formatNumber, formatWithoutPaisa } from '@/utils/number';
import type { SystemDashboardRow, SystemDashboardSearch } from '../types/dashboard.types';
import { getAmNameColumn } from './AmNameColumn';

const numberCell = (value: number) => <span className="tabular-nums">{formatNumber(value)}</span>;
const takaCell = (amount: number) => (
  <span className="tabular-nums">{formatWithoutPaisa(amount)}</span>
);

export function getSystemDashboardColumns(
  dashboardSearch: SystemDashboardSearch,
  searchText: string | undefined,
): ColumnDef<SystemDashboardRow, unknown>[] {
  return [
    createSerialColumn<SystemDashboardRow>(),
    {
      accessorKey: 'name',
      header: 'ZM Name',
      cell: ({ row }) => <MemberNameCell member={row.original} searchText={searchText} />,
    },
    getAmNameColumn<SystemDashboardRow>(),
    {
      accessorKey: 'departmentName',
      header: 'Department',
      cell: ({ row }) => row.original.departmentName ?? '—',
    },
    getAreaColumn<SystemDashboardRow>(),
    {
      accessorKey: 'dcrEntered',
      header: 'DCR Entered',
      cell: ({ row }) => numberCell(row.original.dcrEntered),
    },
    {
      accessorKey: 'dcrApproved',
      header: 'DCR Approved',
      cell: ({ row }) => numberCell(row.original.dcrApproved),
    },
    {
      accessorKey: 'derEntered',
      header: 'DER Entered',
      cell: ({ row }) => numberCell(row.original.derEntered),
    },
    {
      accessorKey: 'derApproved',
      header: 'DER Approved',
      cell: ({ row }) => numberCell(row.original.derApproved),
    },
    {
      accessorKey: 'dcrClaimedAmount',
      header: 'DCR Claimed (Tk)',
      cell: ({ row }) => takaCell(row.original.dcrClaimedAmount),
    },
    {
      accessorKey: 'dcrApprovedAmount',
      header: 'DCR Approved (Tk)',
      cell: ({ row }) => takaCell(row.original.dcrApprovedAmount),
    },
    {
      accessorKey: 'derClaimedAmount',
      header: 'DER Claimed (Tk)',
      cell: ({ row }) => takaCell(row.original.derClaimedAmount),
    },
    {
      accessorKey: 'derApprovedAmount',
      header: 'DER Approved (Tk)',
      cell: ({ row }) => takaCell(row.original.derApprovedAmount),
    },
    {
      accessorKey: 'receivedQty',
      header: 'SD Received',
      cell: ({ row }) => numberCell(row.original.receivedQty),
    },
    {
      accessorKey: 'returnedQty',
      header: 'SD Returned',
      cell: ({ row }) => numberCell(row.original.returnedQty),
    },
    {
      accessorKey: 'deliveredQty',
      header: 'SD Delivered',
      cell: ({ row }) => numberCell(row.original.deliveredQty),
    },
    {
      accessorKey: 'instituteCount',
      header: 'Unique Institutions',
      cell: ({ row }) => numberCell(row.original.instituteCount),
    },
    {
      accessorKey: 'teacherCount',
      header: 'Unique Teachers',
      cell: ({ row }) => numberCell(row.original.teacherCount),
    },
    createActionsColumn<SystemDashboardRow>((member) => (
      <RowViewLink
        to="/dashboards/system/$userId"
        params={{ userId: member.userId }}
        search={dashboardSearch}
        label="Summary"
        aria-label={`Work summary of ${member.name}`}
      />
    )),
  ];
}
